import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { ICampaign } from '~/utils/interface/campaign';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, catchError, handleApiError, notify } from '~/utils/helper';
import { BaseDatePicker, BaseRichEditor, BaseRow, BaseSelect } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { handleCreateCampaign, handleEditCampaign } from '~/thunks/campaign/campaignThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { campaignActions } from '~/thunks/campaign/campaignSlice';
import customerApi from '~/apis/customer';
import { MASTER_DATA_CUSTOMER_TYPE } from '~/utils/constants/master';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { handleGetTemplates } from '~/thunks/template/templateThunk';
import { ITemplate } from '~/utils/interface/template';
import { STATUS_DRAFT, STATUS_SCHEDULED, STATUS_SENT, STATUSES } from '~/utils/constants/campaign';
import dayjs from 'dayjs';
import styles from './EditCampaignDialog.module.scss';
import classNames from 'classnames/bind';
import ModalAlertConfirm from '~/components/common/modal-alert-confirm/ModalAlertConfirm';

const cx = classNames.bind(styles);

type EditCampaignDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: ICampaign;
  children?: React.ReactNode;
};

const initialValues: ICampaign = {
  name: '',
  from: '',
  subject: '',
  content: '',
  toType: [],
  to: [],
  templateId: '',
  status: STATUS_DRAFT,
};

const EditCampaignDialog = (props: EditCampaignDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE, data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/campaign']);
  const dispatch = useAppDispatch();

  //#endregion Declare Hook

  //#region Selector
  const { customerType } = useAppSelector((state) => state.master);
  const { campaignId } = useAppSelector((state) => state.campaign);
  //#endregion Selector

  //#region Declare State
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateCampaign(values)).unwrap();
          dispatch(campaignActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditCampaign({ id: campaignId, params: values })).unwrap();
          dispatch(campaignActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  const [templateList, setTemplateList] = useState<Array<ITemplate>>([]);
  const statusOptions = useMemo(
    () =>
      STATUSES.map((status: string) => ({
        id: status,
        name: t(`statuses.${status}`),
      })),
    [t]
  );
  const [isSentConfirm, setIsSentConfirm] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name), false);
      form.setFieldValue('from', cast(data?.from), false);
      form.setFieldValue('toType', cast(data?.toType), false);
      form.setFieldValue('to', cast(data?.to), false);
      form.setFieldValue('subject', cast(data?.subject), false);
      form.setFieldValue('templateId', cast(data?.templateId), false);
      form.setFieldValue('content', cast(data?.content), false);
      form.setFieldValue('status', cast(data?.status), false);
      data?.sendAt && form.setFieldValue('sendAt', dayjs(data?.sendAt).format('YYYY-MM-DD HH:mm:sss'), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          await dispatch(
            handleGetMasterData({
              type: [MASTER_DATA_CUSTOMER_TYPE],
            })
          ).unwrap();

          const result = await dispatch(handleGetTemplates({ limit: 0, type: 'email' })).unwrap();
          setTemplateList(result.responses as ITemplate[]);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [dispatch, props.open]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };

  const getCustomerEmailList = async (customerType: Array<string>) => {
    try {
      const response = await customerApi.getListCustomer({
        customerType,
        page: 0,
        selectFields: ['email'],
        emailExist: true,
      });
      form.setFieldValue(
        'to',
        response.data.data.responses.map(({ email }) => email)
      );
    } catch (e) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };

  const handleTemplateChange = (value: string) => {
    const selectedTemplate = templateList.find((template) => template.id === value);
    form.setFieldValue('templateId', value);
    form.setFieldValue('content', selectedTemplate ? selectedTemplate.name : '');
  };
  //#endregion Handle Function

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => handleSetOpen(false)}
        fullWidth
        maxWidth='md'
        className={cx('edit-campaign-dialog')}
      >
        <DialogTitle>
          {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
          <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
            <CloseIcon className='w-[10px] h-[10px]' />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.name')}</InputLabel>
                <TextField
                  name='name'
                  placeholder={t('properties.name')}
                  value={form.values?.name}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.name)}
                  helperText={form.errors?.name}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.from')}</InputLabel>
                <TextField
                  name='from'
                  placeholder={t('properties.from')}
                  value={form.values?.from}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.from)}
                  helperText={form.errors?.from}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.toType')}</InputLabel>
                <BaseSelect
                  name='toType'
                  value={form.values.toType}
                  options={customerType}
                  onChange={(e) => {
                    form.setFieldValue('toType', e.target.value);
                    getCustomerEmailList(e.target.value);
                  }}
                  error={Boolean(form.errors?.toType)}
                  multiple
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>
                  {t('properties.to')} ({form.values.to.length} {t('recipients')})
                </InputLabel>
                <Autocomplete
                  multiple
                  options={[]}
                  value={form.values.to}
                  freeSolo
                  onChange={(_event, newValue) => {
                    form.setFieldValue('to', newValue);
                  }}
                  className='multipleline'
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='to'
                      error={Boolean(form.errors.to)}
                      helperText={form.errors.to}
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.subject')}</InputLabel>
                <TextField
                  name='subject'
                  placeholder={t('properties.subject')}
                  value={form.values.subject}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.subject)}
                  helperText={form.errors?.subject}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.templateId')}</InputLabel>
                <BaseSelect
                  options={templateList}
                  name='templateId'
                  value={form.values.templateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  error={Boolean(form.errors?.templateId)}
                  helperText={form.errors?.templateId}
                />
              </Box>
            </BaseRow>
            <Box className='mb-[20px]'>
              <BaseRichEditor
                onCreateNew={(value) => form.setFieldValue('content', value)}
                valueProps={form.values.content}
                isError={Boolean(form.errors?.content)}
                errorMessage={form.errors.content}
              />
            </Box>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.status')}</InputLabel>
                <BaseSelect
                  name='status'
                  value={form.values.status}
                  options={statusOptions}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.status)}
                  helperText={form.errors.status}
                />
              </Box>
              {form.values.status === STATUS_SCHEDULED ? (
                <Box className='w-full'>
                  <InputLabel>{t('scheduledAt')}</InputLabel>
                  <BaseDatePicker
                    value={form.values.sendAt}
                    onChange={(value) => form.setFieldValue(`sendAt`, dayjs(value).format('YYYY-MM-DD HH:mm:ss'))}
                    showTimeInput={true}
                    dateFormat='DD MMM, YYYY hh:mm a'
                    error={Boolean(form.errors.sendAt)}
                    helperText={form.errors.sendAt}
                  />
                </Box>
              ) : (
                <Box className='w-full'></Box>
              )}
            </BaseRow>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button color='secondary' variant='contained' onClick={handleCancel}>
            {t('cancel', { ns: 'admin/common' })}
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              if (
                (mode === DIALOG_MODE_CREATE && form.values.status === STATUS_SENT) ||
                (mode === DIALOG_MODE_EDIT && data?.status !== STATUS_SENT && form.values.status === STATUS_SENT)
              ) {
                return setIsSentConfirm(true);
              }
              form.handleSubmit();
            }}
          >
            {t('save', { ns: 'admin/common' })}
          </Button>
        </DialogActions>
      </Dialog>
      <ModalAlertConfirm
        open={isSentConfirm}
        description={t('sentConfirm.alertDescription')}
        confirm={t('yes', { ns: 'admin/common' })}
        onCancel={() => setIsSentConfirm(false)}
        onConfirm={() => {
          setIsSentConfirm(false);
          form.handleSubmit();
        }}
      />
    </>
  );
};

export default memo(EditCampaignDialog);
